/* BMR UI — the design system as components (F-1…F-12).
   Presentation lives in ui.css on --bmr-* tokens; these wrappers add
   semantics, a11y and the state contracts. One import site: '@/ui'. */
import {
  createContext, useCallback, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react'
import spriteDuotone from '@brand/icons/sprite-duotone.svg?raw'
import { giftIcon } from '@/lib/wheel'
import { PLATFORMS, type PlatformId } from '@/lib/campaign'
import './ui.css'

/* ---------- F-icons · sprite mounted once, used by id ---------- */
export function IconSprite() {
  return <div hidden aria-hidden dangerouslySetInnerHTML={{ __html: spriteDuotone }} />
}
const SPRITE = new Set(['home', 'locations', 'campaigns', 'return-journey', 'experiences', 'rewards', 'destinations', 'style', 'displays', 'customers', 'analytics', 'team', 'billing', 'settings'])
/* UI icons (24-grid, 1.8 stroke) — the brand sprite covers the nav; these cover actions. */
const UI_ICONS: Record<string, string> = {
  plus: 'M12 5v14M5 12h14',
  check: 'M5 12.5l4.5 4.5L19 7',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  back: 'M19 12H5M11 18l-6-6 6-6',
  external: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  edit: 'M4 20h4l10.5-10.5a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 16v4Z',
  duplicate: 'M8 8h11a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1ZM16 8V5a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h3',
  send: 'M4 12l16-8-6 16-3-6-7-2Z',
  bolt: 'M13 2L4 14h7l-1 8 9-12h-7l1-8Z',
  wand: 'M4 20l10-10M14 4l1.5 3.5L19 9l-3.5 1.5L14 14l-1.5-3.5L9 9l3.5-1.5L14 4ZM19 15l.8 1.8L21.6 17.5l-1.8.8L19 20l-.8-1.8L16.4 17.5l1.8-.8L19 15Z',
  print: 'M7 8V4h10v4M7 17H4v-6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6h-3M7 14h10v6H7z',
  phone: 'M8 3h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1ZM11 18h2',
  link: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1',
  nfc: 'M6 8.5a8 8 0 0 1 0 7M9.5 10a4 4 0 0 1 0 4M13 6.5a11 11 0 0 1 0 11M16.5 4.5a14 14 0 0 1 0 15',
  search: 'M11 4a7 7 0 1 1 0 14 7 7 0 0 1 0-14ZM20 20l-4-4',
  bell: 'M18 15V10a6 6 0 1 0-12 0v5l-1.7 2.6a.6.6 0 0 0 .5.9h14.4a.6.6 0 0 0 .5-.9L18 15ZM10 20a2 2 0 0 0 4 0',
  help: 'M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18ZM9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .8-1 1.7M12 17h.01',
  plug: 'M9 3v4M15 3v4M6 7h12v4a6 6 0 0 1-12 0V7ZM12 17v4',
  message: 'M4 5h16v11H8l-4 4V5Z',
  more: 'M6 12h.01M12 12h.01M18 12h.01',
  chevron: 'M9 6l6 6-6 6',
  close: 'M6 6l12 12M18 6L6 18',
  alert: 'M12 3l10 18H2L12 3ZM12 10v4M12 17h.01',
  info: 'M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18ZM12 11v5M12 8h.01',
  screen: 'M3 5h18v11H3zM8 20h8M12 16v4',
  qr: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2v2h-2zM14 18h2v2h-2zM18 18h2v2h-2z',
  play: 'M7 5l12 7-12 7V5Z',
  pause: 'M8 5h3v14H8zM13 5h3v14h-3z',
  gift: 'M4 11h16v9H4zM3 7h18v4H3zM12 7v13M12 7c-2-4-6-3-6-1s4 1 6 1ZM12 7c2-4 6-3 6-1s-4 1-6 1Z',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 21a8 8 0 0 1 16 0',
  menu: 'M4 7h16M4 12h16M4 17h16',
  lock: 'M6 11h12v10H6zM9 11V7a3 3 0 0 1 6 0v4',
  unlock: 'M6 11h12v10H6zM9 11V7a3 3 0 0 1 5.8-1',
  building: 'M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 9h4a1 1 0 0 1 1 1v11M8 8h3M8 12h3M8 16h3M3 21h18',
  chevron_down: 'M6 9l6 6 6-6',
  chevron_up: 'M6 15l6-6 6 6',
}
export function Icon({ name, size = 16 }: { name: string; size?: number }) {
  if (SPRITE.has(name)) return (
    <svg className="ic" width={size} height={size} aria-hidden focusable="false">
      <use href={`#bmr-ic-${name}`} />
    </svg>
  )
  const d = UI_ICONS[name] ?? UI_ICONS.info
  return (
    <svg className="ic" width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
      <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/* ---------- F-3 · Button ---------- */
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'quiet' | 'danger' | 'ghost'
  size?: 'md' | 'sm'
  wide?: boolean
}
export function Button({ variant = 'quiet', size = 'md', wide, className = '', ...rest }: ButtonProps) {
  const cls = ['btn', `btn-${variant}`, size === 'sm' && 'sm', wide && 'w', className]
    .filter(Boolean).join(' ')
  return <button type="button" className={cls} {...rest} />
}

/* ---------- F-5 · Pill — the word always travels with the colour ---------- */
const PILL_MARK = { ok: '●', done: '✓', warn: '◔', crit: '✕', spark: '★', ink: '' } as const
export type PillTone = keyof typeof PILL_MARK
/** F-12: word + mark, never colour alone. `mark` overrides the tone’s default
 *  glyph (⊘ paused, ◷ suppressed, ✎ draft…); tone "ink" is the bare-word
 *  chip (« obligatoire », « verrouillé ») with no mark unless given one. */
export function Pill({ tone, mark, children }: { tone: PillTone; mark?: string; children: ReactNode }) {
  const m = mark ?? PILL_MARK[tone]
  return <span className={`pill pill-${tone}`}>{m ? m + ' ' : ''}{children}</span>
}

/* ---------- F-4 · fields ---------- */
export function Field({ label, help, error, children }:
  { label: string; help?: string; error?: string; children: (id: string) => ReactNode }) {
  const id = useId()
  return (
    <label className={'field' + (error ? ' err' : '')} htmlFor={id}>
      <span className="f-label">{label}</span>
      {children(id)}
      {(error ?? help) && <span className="f-help">{error ?? help}</span>}
    </label>
  )
}
export const Input = (p: InputHTMLAttributes<HTMLInputElement>) =>
  <input className="in" {...p} />
export const Textarea = (p: TextareaHTMLAttributes<HTMLTextAreaElement>) =>
  <textarea className="in" {...p} />
export const Select = (p: SelectHTMLAttributes<HTMLSelectElement>) =>
  <select className="in" {...p} />
export const Switch = (p: InputHTMLAttributes<HTMLInputElement>) =>
  <input type="checkbox" role="switch" className="switch" {...p} />
export const Checkbox = (p: InputHTMLAttributes<HTMLInputElement>) =>
  <input type="checkbox" className="chk" {...p} />

/* ---------- notes: candour / ok / crit ---------- */
export function Note({ tone = 'candour', children, 'data-testid': testid }:
  { tone?: 'candour' | 'ok' | 'crit'; children: ReactNode; 'data-testid'?: string }) {
  return <div className={`note-row note-${tone}`} role={tone === 'crit' ? 'alert' : 'note'} data-testid={testid}><div>{children}</div></div>
}

/* ---------- F-6 · cards & tiles ---------- */
export const Card = ({ children, className = '', 'data-testid': testid }: { children: ReactNode; className?: string; 'data-testid'?: string }) =>
  <div className={`card ${className}`} data-testid={testid}>{children}</div>
export function Tile({ label, value, hint, hero }:
  { label: ReactNode; value: ReactNode; hint?: ReactNode; hero?: boolean }) {
  return (
    <div className={'tile' + (hero ? ' hero' : '')}>
      <div className="tile-label">{label}</div>
      <div className="tile-value">{value}</div>
      {hint && <div className="tile-hint">{hint}</div>}
    </div>
  )
}
export const Tiles = ({ children }: { children: ReactNode }) => <div className="tiles">{children}</div>

/* ---------- F-12 · list row ---------- */
export function ListRow({ title, meta, end, icon }:
  { title: ReactNode; meta?: ReactNode; end?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="listrow">
      {icon && <span className="listrow-ic" aria-hidden>{icon}</span>}
      <div className="listrow-main">
        <span className="listrow-title">{title}</span>
        {meta && <span className="listrow-meta">{meta}</span>}
      </div>
      {end && <span className="listrow-end">{end}</span>}
    </div>
  )
}

/** One container per list: single border, dividers between rows — rows are not cards (design pass 04/09). */
export const List = ({ children, className = '' }: { children: ReactNode; className?: string }) =>
  <div className={`list ${className}`}>{children}</div>

/* ---------- the establishment context, as a label the chrome provides (D-31) ---------- */
export const ScopeLabelCtx = createContext<string | null>(null)
export const useScopeLabel = () => useContext(ScopeLabelCtx)

/* ---------- page header — title, one-line description, actions, tabs (design pass 04/09);
   in a context the crumb starts with the establishment (« Paris 9 › Résultats », D-31) ---------- */
export function PageHeader({ title, description, actions, aside, crumb, tabs, next }:
  { title: ReactNode; description?: ReactNode; actions?: ReactNode; aside?: ReactNode; crumb?: ReactNode; tabs?: ReactNode; next?: ReactNode }) {
  const scopeLabel = useScopeLabel()
  return (
    <header className="ph">
      {(crumb || scopeLabel) && <div className="ph-crumb">{scopeLabel && <><span className="ph-scope">{scopeLabel}</span> <span aria-hidden>›</span> </>}{crumb && <>{crumb} <span aria-hidden>›</span></>}{!crumb && scopeLabel && <span>{title}</span>}</div>}
      <div className="ph-row">
        <div className="ph-main">
          <h1 className="ph-title">{title}</h1>
          {aside && <div className="ph-aside">{aside}</div>}
        </div>
        {actions && <div className="ph-actions">{actions}</div>}
      </div>
      {description && <p className="ph-desc">{description}</p>}
      {next && <p className="ph-next"><b>Et ensuite&nbsp;:</b> {next}</p>}
      {tabs && <div className="ph-tabs">{tabs}</div>}
    </header>
  )
}
/** Underline tabs — sections of one page. Distinct from filter chips (Segmented) and buttons. */
export function Tabs({ value, onChange, options, disabled }: { value: string; onChange: (v: string) => void; options: [string, string][]
  /** lib-pack-21 (resultats-vide-vet) · the strip as a picture: no tab takes the focus or a click (an example page) */
  disabled?: boolean }) {
  return (
    <div className="ptabs" role="tablist">
      {options.map(([v, label]) => (
        <button key={v} type="button" role="tab" aria-selected={value === v} disabled={disabled} className={'ptab' + (value === v ? ' on' : '')} onClick={() => onChange(v)}>{label}</button>
      ))}
    </div>
  )
}
/** Segmented control — a filter over one list (« En cours · Toutes · Archivées »). */
export function Segmented({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div className="seg" role="group">
      {options.map(([v, label]) => (
        <button key={v} type="button" aria-pressed={value === v} className={'seg-b' + (value === v ? ' on' : '')} onClick={() => onChange(v)}>{label}</button>
      ))}
    </div>
  )
}

/* ---------- F-9 · empty / loading / error ---------- */
export function EmptyState({ title, why, action, children }:
  { title: ReactNode; why?: ReactNode; action?: ReactNode; children?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {children}
      {why && <p className="empty-why">{why}</p>}
      {action && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{action}</div>}
    </div>
  )
}
export const Skeleton = ({ width = '100%' }: { width?: string | number }) =>
  <div className="skel" style={{ width }} aria-hidden />
export function ErrorState({ what, lost, action }:
  { what: ReactNode; lost?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty" role="alert">
      <p className="empty-title">{what}</p>
      <p className="empty-why">{lost ?? 'Rien n’a été perdu.'}</p>
      {action && <div style={{ display: 'flex', gap: 10 }}>{action}</div>}
    </div>
  )
}

/* ---------- F-11 · modal & confirm ---------- */
export function Modal({ open, title, onClose, children, actions }:
  { open: boolean; title: ReactNode; onClose: () => void; children: ReactNode; actions?: ReactNode }) {
  /* lib-pack-21 (per-visit-e2e-vet) · the dialog is named by its title for assistive tech */
  const titleId = useId()
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="modal-veil" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h3 id={titleId}>{title}</h3>
        {children}
        {actions && <div className="modal-actions">{actions}</div>}
      </div>
    </div>
  )
}

/* ---------- F-12 · toast (archive gets undo — F-11 doctrine) ----------
   wf00: bottom-left · 5 s, 8 s when it carries an action (undo or open). */
interface ToastState {
  message: string
  undo?: () => void
  /** a follow-up like « Ouvrir » after duplicating — never destructive */
  open?: { label: string; go: () => void }
}
const ToastCtx = createContext<(t: ToastState) => void>(() => {})
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null)
  const timer = useRef<number | null>(null)
  const left = useRef(0); const since = useRef(0)
  const arm = useCallback((t: ToastState, ms: number) => {
    if (timer.current) window.clearTimeout(timer.current)
    since.current = Date.now(); left.current = ms
    timer.current = window.setTimeout(() => setToast(cur => (cur === t ? null : cur)), ms)
  }, [])
  const show = useCallback((t: ToastState) => {
    setToast(t)
    arm(t, t.undo || t.open ? 8000 : 5000)
  }, [arm])
  /* D-37 (05/09): a toast closes — ✕, Escape — and waits while the pointer or the focus is on it */
  useEffect(() => {
    if (!toast) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setToast(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toast])
  const pause = () => { if (timer.current) { window.clearTimeout(timer.current); timer.current = null; left.current = Math.max(1500, left.current - (Date.now() - since.current)) } }
  const resume = () => { if (toast && !timer.current) arm(toast, left.current) }
  return (
    <ToastCtx.Provider value={show}>
      {children}
      {toast && (
        <div className="toast" role="status" onMouseEnter={pause} onMouseLeave={resume} onFocus={pause} onBlur={resume}>
          <span className="toast-msg">{toast.message}</span>
          {toast.undo && (
            <button type="button" className="btn btn-ghost"
              onClick={() => { toast.undo?.(); setToast(null) }}>Annuler</button>
          )}
          {toast.open && (
            <button type="button" className="btn btn-ghost"
              onClick={() => { toast.open?.go(); setToast(null) }}>{toast.open.label}</button>
          )}
          <button type="button" className="toast-x" aria-label="Fermer la notification" onClick={() => setToast(null)}>×</button>
        </div>
      )}
    </ToastCtx.Provider>
  )
}
export const useToast = () => useContext(ToastCtx)

/* ---------- D-9 · Hint — the ⓘ definition layer (owner decisions 02/09).
   One component, definitions verbatim from locked copy, every concept named
   on first encounter. Click-toggle (works on touch), Escape closes. ---------- */
export function Hint({ label = 'Définition', children }:
  { label?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])
  return (
    <span className="hint">
      <button type="button" className="hint-btn" aria-label={label} aria-expanded={open}
        onClick={() => setOpen(o => !o)}>i</button>
      {open && <span role="note" className="hint-pop">{children}</span>}
    </span>
  )
}

/* ---------- Meter — quiet progress against a stated target (TC/ON/BI).
   Word + figure always beside the bar — never colour alone (F-12). ---------- */
export function Meter({ value, max, label }: { value: number; max: number; label?: ReactNode }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0
  return (
    <div className="meter">
      <div className="meter-track" role="meter" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
        <div className="meter-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="meter-text">{label ?? `${value} / ${max}`}</span>
    </div>
  )
}

/* ---------- misc ---------- */
/** F-12 stepper. Long journeys elide (ML-01 draws « 1 › 2 › 3 › … › 9 »):
 *  beyond 6 steps, keep the first three, the neighbours of `current`, and
 *  the last — collapsed gaps render a real « … », never a missing step. */
export function Steps({ items, current }: { items: string[]; current: number }) {
  const keep = items.length <= 6
    ? items.map((_, i) => i)
    : items.map((_, i) => i).filter(i =>
        i < 3 || i === items.length - 1 || Math.abs(i - current) <= 1)
  const parts: Array<number | 'gap'> = []
  keep.forEach((i, k) => {
    if (k > 0 && i - keep[k - 1] > 1) parts.push('gap')
    parts.push(i)
  })
  return (
    <span className="steps">
      {parts.map((p, k) => p === 'gap'
        ? <span key={`g${k}`} aria-hidden>… <i> › </i></span>
        : <span key={items[p]} className={p === current ? 'on' : p < current ? 'done' : ''}>
            {p < current ? '✓ ' : `${p + 1} `}{items[p]}{k < parts.length - 1 && <i> › </i>}
          </span>)}
    </span>
  )
}
export function KV({ rows }: { rows: Array<[ReactNode, ReactNode]> }) {
  return <dl style={{ margin: 0 }}>{rows.map(([k, v], i) =>
    <div className="kv" key={i}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
}
export const Rule = ({ children }: { children?: ReactNode }) =>
  <div className="rule">{children}</div>
export const Kbd = ({ children }: { children: ReactNode }) =>
  <kbd className="kbd">{children}</kbd>

/* ---------- D-35 (05/09) · a gift type's icon — a tinted tile with the brand's stroke
   (the duotone sprite in packages/brand replaces the prototype set at M2) ---------- */
export function GiftIcon({ id, size = 34 }: { id?: string; size?: number }) {
  const ic = giftIcon(id)
  const inner = Math.round(size * 0.58)
  return (
    <span className="gift-ic" style={{ width: size, height: size }} aria-hidden title={ic.label}>
      <svg width={inner} height={inner} viewBox="0 0 24 24" fill="none" focusable="false">
        <path d={ic.d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  )
}

/* ---------- PlatformMark — the mark of a review platform (batch 12, one copy for the whole app) ----------
   Letters on the platform's colour (`PLATFORMS` in @/lib/campaign) until counsel confirms the official
   assets for the customer's screen (One-Location sheet §10.1); a custom link shows its own icon.
   Used by « Demandes › Liens d'avis », the stepper's step 3 and Builder A's Destinations —
   the day a real logo lands, it lands here and nowhere else. */
export function PlatformMark({ platform, icon, size = 26 }: { platform: PlatformId; icon?: string; size?: number }) {
  const p = PLATFORMS.find(x => x.id === platform) ?? PLATFORMS[0]
  return <span aria-hidden style={{ display: 'inline-grid', placeItems: 'center', width: size, height: size, borderRadius: Math.round(size / 3.7), background: p.color, color: p.ink ?? '#fff', font: `800 ${Math.round(size * 0.42)}px 'Plus Jakarta Sans', sans-serif`, flex: 'none' }}>{platform === 'custom' ? (icon ?? p.mark) : p.mark}</span>
}
